import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useMinistry } from '@/contexts/MinistryContext';
import { toast } from 'sonner';
import { Search, UserCheck, LogOut as CheckOut, Plus, Trash2, Edit, Filter, Calendar, Users, Eye, Printer, CheckSquare, Square, UserPlus, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import PrintLabels from '@/components/PrintLabels';
import PhotoUpload from '@/components/PhotoUpload';

const CheckIn = () => {
  const { currentMinistry, loading: ministryLoading } = useMinistry();
  const [search, setSearch] = useState('');
  const [manageSearch, setManageSearch] = useState(''); // Busca na aba Gerenciar Presenças
  const [checkinListSearch, setCheckinListSearch] = useState(''); // Busca na lista de check-in
  const [adolescents, setAdolescents] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventCheckins, setEventCheckins] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [newEventDialog, setNewEventDialog] = useState(false);
  const [editCheckinDialog, setEditCheckinDialog] = useState(false);
  const [selectedCheckin, setSelectedCheckin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newEventData, setNewEventData] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    type: 'culto'
  });
  
  // Estados para cadastro rápido de visitante
  const [quickRegisterDialog, setQuickRegisterDialog] = useState(false);
  const [quickRegisterData, setQuickRegisterData] = useState({
    name: '',
    photo_url: '',
    birth_date: '',
    cpf: '',
    phone: '',
    gender: '',
    guardian_name: '',
    guardian_phone: '',
    guardian_relationship: '',
    guardian2_name: '',
    guardian2_phone: '',
    has_special_needs: false,
    notes: ''
  });
  const [quickRegisterLoading, setQuickRegisterLoading] = useState(false);
  
  // Estados para completar cadastro antes do check-in
  const [completeDataDialog, setCompleteDataDialog] = useState(false);
  const [adolescentToCheckin, setAdolescentToCheckin] = useState(null);
  const [completeDataForm, setCompleteDataForm] = useState({});
  const [missingFields, setMissingFields] = useState([]);
  const [savingCompleteData, setSavingCompleteData] = useState(false);
  
  // Estados para impressão de etiquetas
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [checkinsToPrint, setCheckinsToPrint] = useState([]);
  const [printOnCheckin, setPrintOnCheckin] = useState(() => {
    const saved = localStorage.getItem('printOnCheckin');
    return saved !== null ? saved === 'true' : true;
  });
  const [selectedForPrint, setSelectedForPrint] = useState(new Set());
  const [labelLogo, setLabelLogo] = useState(localStorage.getItem('labelLogo') || '');

  useEffect(() => {
    if (!ministryLoading && currentMinistry?.id) {
      fetchEvents();
    }
  }, [currentMinistry?.id, ministryLoading]);

  useEffect(() => {
    if (selectedEvent) {
      fetchEventCheckins();
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (search.length > 2) {
      const debounce = setTimeout(() => {
        searchAdolescents();
      }, 300);
      return () => clearTimeout(debounce);
    } else {
      setAdolescents([]);
      setShowResults(false);
    }
  }, [search]);

  const fetchEvents = async () => {
    if (!currentMinistry?.id) return;
    try {
      const response = await axios.get(`${API}/events?ministry_id=${currentMinistry.id}`);
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEvent(response.data[0]);
      }
    } catch (error) {
      toast.error('Erro ao carregar eventos');
    }
  };

  const searchAdolescents = async () => {
    if (!currentMinistry?.id) return;
    try {
      const response = await axios.get(`${API}/adolescents?search=${search}&ministry_id=${currentMinistry.id}`);
      setAdolescents(response.data);
      setShowResults(true);
    } catch (error) {
      toast.error('Erro ao buscar adolescentes');
    }
  };

  const fetchEventCheckins = async () => {
    if (!selectedEvent) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/checkins/event/${selectedEvent.id}`);
      setEventCheckins(response.data);
    } catch (error) {
      console.error('Error fetching event checkins:', error);
    } finally {
      setLoading(false);
    }
  };

  // Campos obrigatórios para check-in
  const requiredFields = [
    { key: 'photo_url', label: 'Foto' },
    { key: 'name', label: 'Nome' },
    { key: 'phone', label: 'Telefone' },
    { key: 'cpf', label: 'CPF' },
    { key: 'birth_date', label: 'Data de Nascimento' },
    { key: 'guardian_name', label: 'Nome do Responsável' },
    { key: 'guardian_phone', label: 'Telefone do Responsável' },
    { key: 'gender', label: 'Gênero' }
  ];

  // Verifica campos faltantes do adolescente
  const checkMissingFields = (adolescent) => {
    return requiredFields.filter(field => {
      const value = adolescent[field.key];
      // Considera faltante se: não existe, é vazio, é 'None' (string literal), ou é null
      return !value || value === '' || value === 'None' || value === 'null';
    });
  };

  const handleCheckin = async (adolescent) => {
    if (!selectedEvent) {
      toast.error('Selecione um evento primeiro');
      return;
    }
    
    // Verificar campos faltantes
    const missing = checkMissingFields(adolescent);
    
    if (missing.length > 0) {
      // Abrir modal para completar cadastro
      setAdolescentToCheckin(adolescent);
      setMissingFields(missing);
      setCompleteDataForm({
        photo_url: adolescent.photo_url || '',
        name: adolescent.name || '',
        phone: adolescent.phone || '',
        cpf: adolescent.cpf || '',
        birth_date: adolescent.birth_date || '',
        guardian_name: adolescent.guardian_name || '',
        guardian_phone: adolescent.guardian_phone || '',
        gender: adolescent.gender || ''
      });
      setCompleteDataDialog(true);
      return;
    }
    
    // Se todos os campos estão preenchidos, fazer check-in diretamente
    await performCheckin(adolescent);
  };

  const performCheckin = async (adolescent) => {
    try {
      const response = await axios.post(`${API}/checkins`, {
        adolescent_id: adolescent.id,
        event_id: selectedEvent.id
      });
      
      toast.success(`${adolescent.name} fez check-in com sucesso!`);
      setSearch('');
      setShowResults(false);
      fetchEventCheckins();
      
      // Abrir etiqueta para impressão APENAS se o checkbox estiver ativado
      if (printOnCheckin) {
        const newCheckin = {
          ...response.data,
          adolescent_name: adolescent.name,
          guardian_name: adolescent.guardian_name || '',
          birth_date: adolescent.birth_date || '',
          adolescent_status: adolescent.status || '',
          has_special_needs: adolescent.has_special_needs || false
        };
        setCheckinsToPrint([newCheckin]);
        setPrintDialogOpen(true);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer check-in');
    }
  };

  const handleSaveCompleteData = async () => {
    if (!adolescentToCheckin) return;
    
    // Validar que todos os campos obrigatórios estão preenchidos
    const stillMissing = missingFields.filter(field => !completeDataForm[field.key] || completeDataForm[field.key] === '');
    if (stillMissing.length > 0) {
      toast.error(`Por favor, preencha: ${stillMissing.map(f => f.label).join(', ')}`);
      return;
    }
    
    try {
      setSavingCompleteData(true);
      
      // Mesclar dados atuais do adolescente com os novos dados do formulário
      const updatedAdolescent = { ...adolescentToCheckin, ...completeDataForm };
      
      // Enviar todos os campos obrigatórios para a API
      const updatePayload = {
        name: updatedAdolescent.name,
        birth_date: updatedAdolescent.birth_date,
        cpf: updatedAdolescent.cpf,
        phone: updatedAdolescent.phone,
        gender: updatedAdolescent.gender,
        guardian_name: updatedAdolescent.guardian_name,
        guardian_phone: updatedAdolescent.guardian_phone,
        status: updatedAdolescent.status,
        // Campos opcionais
        ministry_id: updatedAdolescent.ministry_id || currentMinistry?.id,
        photo_url: updatedAdolescent.photo_url || '',
        guardian_relationship: updatedAdolescent.guardian_relationship || '',
        guardian2_name: updatedAdolescent.guardian2_name || '',
        guardian2_phone: updatedAdolescent.guardian2_phone || '',
        has_special_needs: updatedAdolescent.has_special_needs || false,
        notes: updatedAdolescent.notes || ''
      };
      
      await axios.put(`${API}/adolescents/${adolescentToCheckin.id}`, updatePayload);
      
      // Fechar modal e fazer check-in
      setCompleteDataDialog(false);
      setAdolescentToCheckin(null);
      setMissingFields([]);
      
      // Atualizar a lista de adolescentes local
      setAdolescents(prev => prev.map(a => a.id === updatedAdolescent.id ? updatedAdolescent : a));
      
      toast.success('Cadastro atualizado!');
      
      // Fazer o check-in
      await performCheckin(updatedAdolescent);
      
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      // Tratar caso onde detail é um array de validação do Pydantic
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) 
          ? errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
          : 'Erro ao atualizar cadastro');
      toast.error(errorMessage);
    } finally {
      setSavingCompleteData(false);
    }
  };

  const handleCheckOut = async (checkinId) => {
    try {
      await axios.put(`${API}/checkins/${checkinId}/checkout`);
      toast.success('Check-out realizado com sucesso!');
      fetchEventCheckins();
    } catch (error) {
      toast.error('Erro ao fazer check-out');
    }
  };

  const handleMassCheckout = async () => {
    if (selectedForPrint.size === 0) {
      toast.error('Selecione pelo menos um check-in');
      return;
    }
    
    // Filtrar apenas os que estão com status checked_in
    const checkinsToCheckout = eventCheckins.checkins.filter(
      c => selectedForPrint.has(c.id) && c.status === 'checked_in'
    );
    
    if (checkinsToCheckout.length === 0) {
      toast.error('Nenhum dos selecionados está com status "Presente"');
      return;
    }
    
    if (!window.confirm(`Confirma o check-out de ${checkinsToCheckout.length} pessoa(s)?`)) {
      return;
    }
    
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const checkin of checkinsToCheckout) {
        try {
          await axios.put(`${API}/checkins/${checkin.id}/checkout`);
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Check-out realizado para ${successCount} pessoa(s)!`);
      }
      if (errorCount > 0) {
        toast.error(`Erro em ${errorCount} check-out(s)`);
      }
      
      setSelectedForPrint(new Set());
      fetchEventCheckins();
    } catch (error) {
      toast.error('Erro ao fazer check-out em massa');
    }
  };

  const handleDeleteCheckin = async (checkinId) => {
    if (!window.confirm('Tem certeza que deseja excluir este check-in?')) return;
    
    try {
      await axios.delete(`${API}/checkins/${checkinId}`);
      toast.success('Check-in excluído com sucesso!');
      fetchEventCheckins();
    } catch (error) {
      toast.error('Erro ao excluir check-in');
    }
  };

  const handleEditCheckin = (checkin) => {
    setSelectedCheckin({
      ...checkin,
      checkin_time_local: checkin.checkin_time ? format(parseISO(checkin.checkin_time), "yyyy-MM-dd'T'HH:mm") : '',
      checkout_time_local: checkin.checkout_time ? format(parseISO(checkin.checkout_time), "yyyy-MM-dd'T'HH:mm") : ''
    });
    setEditCheckinDialog(true);
  };

  const handleSaveCheckin = async () => {
    try {
      const updateData = {
        status: selectedCheckin.status,
        notes: selectedCheckin.notes,
        is_visitor: selectedCheckin.is_visitor
      };
      
      if (selectedCheckin.checkin_time_local) {
        updateData.checkin_time = new Date(selectedCheckin.checkin_time_local).toISOString();
      }
      if (selectedCheckin.checkout_time_local) {
        updateData.checkout_time = new Date(selectedCheckin.checkout_time_local).toISOString();
      }
      
      await axios.put(`${API}/checkins/${selectedCheckin.id}`, updateData);
      toast.success('Check-in atualizado com sucesso!');
      setEditCheckinDialog(false);
      fetchEventCheckins();
    } catch (error) {
      toast.error('Erro ao atualizar check-in');
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events?ministry_id=${currentMinistry?.id}`, newEventData);
      toast.success('Evento criado com sucesso!');
      setNewEventDialog(false);
      setNewEventData({ name: '', date: new Date().toISOString().split('T')[0], type: 'culto' });
      fetchEvents();
    } catch (error) {
      toast.error('Erro ao criar evento');
    }
  };

  // Cadastro rápido de visitante com check-in automático
  const handleQuickRegister = async (e) => {
    e.preventDefault();
    
    if (!selectedEvent) {
      toast.error('Selecione um evento primeiro');
      return;
    }
    
    setQuickRegisterLoading(true);
    try {
      const response = await axios.post(`${API}/checkins/quick-register`, {
        ...quickRegisterData,
        event_id: selectedEvent.id,
        ministry_id: currentMinistry?.id
      });
      
      toast.success(`${response.data.adolescent.name} cadastrado(a) e check-in realizado!`);
      
      // Fecha o dialog e limpa o formulário
      setQuickRegisterDialog(false);
      setQuickRegisterData({
        name: '',
        photo_url: '',
        birth_date: '',
        cpf: '',
        phone: '',
        gender: '',
        guardian_name: '',
        guardian_phone: '',
        guardian_relationship: '',
        guardian2_name: '',
        guardian2_phone: '',
        has_special_needs: false,
        notes: ''
      });
      
      // Atualiza a lista de check-ins
      fetchEventCheckins();
      
      // Se a opção de imprimir estiver ativa, abre o dialog de impressão
      if (printOnCheckin) {
        setCheckinsToPrint([response.data.checkin]);
        setPrintDialogOpen(true);
      }
    } catch (error) {
      console.error('Erro no cadastro rápido:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar visitante');
    } finally {
      setQuickRegisterLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="fade-in" data-testid="checkin-page">
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-1 lg:mb-2" style={{fontFamily: 'Space Grotesk'}}>Check-in</h1>
        <p className="text-sm lg:text-lg text-gray-600">Registre e gerencie a presença dos adolescentes</p>
      </div>

      <Tabs defaultValue="checkin">
        <TabsList className="grid w-full grid-cols-2 mb-4 lg:mb-6">
          <TabsTrigger value="checkin" className="text-xs sm:text-sm">Fazer Check-in</TabsTrigger>
          <TabsTrigger value="manage" className="text-xs sm:text-sm">Gerenciar Presenças</TabsTrigger>
        </TabsList>

        <TabsContent value="checkin">
          {/* Event Selection */}
          <div className="card mb-4 lg:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <Label className="text-base lg:text-lg font-semibold">Evento Atual</Label>
              <Dialog open={newEventDialog} onOpenChange={setNewEventDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center space-x-2 w-full sm:w-auto" data-testid="create-event-button">
                    <Plus size={16} />
                    <span>Novo Evento</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Criar Novo Evento</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateEvent} className="space-y-4" data-testid="event-form">
                    <div>
                      <Label>Nome do Evento</Label>
                      <Input
                        value={newEventData.name}
                        onChange={(e) => setNewEventData({...newEventData, name: e.target.value})}
                        required
                        placeholder="Ex: Culto Pulse"
                        data-testid="event-name-input"
                      />
                    </div>
                    <div>
                      <Label>Data</Label>
                      <Input
                        type="date"
                        value={newEventData.date}
                        onChange={(e) => setNewEventData({...newEventData, date: e.target.value})}
                        required
                        data-testid="event-date-input"
                      />
                    </div>
                    <div className="flex justify-end space-x-3">
                      <Button type="button" variant="outline" onClick={() => setNewEventDialog(false)}>Cancelar</Button>
                      <Button type="submit" className="btn-primary" data-testid="event-submit-button">Criar</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <select
              className="input-field"
              value={selectedEvent?.id || ''}
              onChange={(e) => {
                const event = events.find(ev => ev.id === e.target.value);
                setSelectedEvent(event);
              }}
              data-testid="event-select"
            >
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name} - {format(parseISO(event.date), "dd/MM/yyyy", { locale: ptBR })}
                </option>
              ))}
            </select>
          </div>

          {/* Stats */}
          {eventCheckins && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="stat-card">
                <div className="stat-value">{eventCheckins.total}</div>
                <div className="stat-label">Total de Presenças</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{eventCheckins.members + eventCheckins.visitors}</div>
                <div className="stat-label">Membros</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{eventCheckins.first_time_visitors}</div>
                <div className="stat-label">Visitantes 1ª Vez</div>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="card mb-6 relative z-30 overflow-visible">
            <div className="flex flex-col gap-3 mb-3">
              <Label className="text-lg font-semibold">Buscar Adolescente para Check-in</Label>
              
              <div className="flex flex-wrap items-center gap-2">
                {/* Botão Novo Visitante */}
                <Dialog open={quickRegisterDialog} onOpenChange={setQuickRegisterDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex items-center gap-1 text-green-600 border-green-200 hover:bg-green-50 text-xs sm:text-sm whitespace-nowrap"
                      disabled={!selectedEvent}
                      data-testid="quick-register-button"
                    >
                      <UserPlus size={16} className="shrink-0" />
                      <span>Novo Visitante</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <UserPlus size={20} className="text-green-600" />
                        Cadastro de Novo Visitante
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleQuickRegister} className="space-y-4" data-testid="quick-register-form">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                        <strong>Primeira vez no evento?</strong> Preencha os dados e o check-in será feito automaticamente.
                      </div>
                      
                      {/* Foto */}
                      <div className="flex justify-center">
                        <PhotoUpload
                          currentPhotoUrl={quickRegisterData.photo_url}
                          onPhotoChange={(url) => setQuickRegisterData({...quickRegisterData, photo_url: url})}
                        />
                      </div>
                      
                      {/* Nome */}
                      <div>
                        <Label>Nome Completo *</Label>
                        <Input
                          value={quickRegisterData.name}
                          onChange={(e) => setQuickRegisterData({...quickRegisterData, name: e.target.value})}
                          required
                          placeholder="Digite o nome completo"
                          data-testid="quick-register-name"
                        />
                      </div>
                      
                      {/* Data Nascimento e Gênero */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Data de Nascimento *</Label>
                          <Input
                            type="date"
                            value={quickRegisterData.birth_date}
                            onChange={(e) => setQuickRegisterData({...quickRegisterData, birth_date: e.target.value})}
                            required
                            data-testid="quick-register-birthdate"
                          />
                        </div>
                        <div>
                          <Label>Gênero *</Label>
                          <Select 
                            value={quickRegisterData.gender} 
                            onValueChange={(value) => setQuickRegisterData({...quickRegisterData, gender: value})}
                          >
                            <SelectTrigger data-testid="quick-register-gender">
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="masculino">Masculino</SelectItem>
                              <SelectItem value="feminino">Feminino</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* CPF e Telefone */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>CPF</Label>
                          <Input
                            value={quickRegisterData.cpf}
                            onChange={(e) => setQuickRegisterData({...quickRegisterData, cpf: e.target.value})}
                            placeholder="000.000.000-00"
                            data-testid="quick-register-cpf"
                          />
                        </div>
                        <div>
                          <Label>Telefone</Label>
                          <Input
                            value={quickRegisterData.phone}
                            onChange={(e) => setQuickRegisterData({...quickRegisterData, phone: e.target.value})}
                            placeholder="(00) 00000-0000"
                            data-testid="quick-register-phone"
                          />
                        </div>
                      </div>
                      
                      {/* Separador - Responsável 1 */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold text-gray-700 mb-3">Responsável Principal</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Nome do Responsável *</Label>
                            <Input
                              value={quickRegisterData.guardian_name}
                              onChange={(e) => setQuickRegisterData({...quickRegisterData, guardian_name: e.target.value})}
                              required
                              placeholder="Nome completo"
                              data-testid="quick-register-guardian-name"
                            />
                          </div>
                          <div>
                            <Label>Telefone *</Label>
                            <Input
                              value={quickRegisterData.guardian_phone}
                              onChange={(e) => setQuickRegisterData({...quickRegisterData, guardian_phone: e.target.value})}
                              required
                              placeholder="(00) 00000-0000"
                              data-testid="quick-register-guardian-phone"
                            />
                          </div>
                        </div>
                        <div className="mt-3">
                          <Label>Grau de Parentesco</Label>
                          <Select 
                            value={quickRegisterData.guardian_relationship} 
                            onValueChange={(value) => setQuickRegisterData({...quickRegisterData, guardian_relationship: value})}
                          >
                            <SelectTrigger data-testid="quick-register-guardian-relationship">
                              <SelectValue placeholder="Selecione..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pai">Pai</SelectItem>
                              <SelectItem value="mae">Mãe</SelectItem>
                              <SelectItem value="avo">Avô/Avó</SelectItem>
                              <SelectItem value="tio">Tio/Tia</SelectItem>
                              <SelectItem value="irmao">Irmão/Irmã</SelectItem>
                              <SelectItem value="padrasto">Padrasto/Madrasta</SelectItem>
                              <SelectItem value="outro">Outro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Separador - Responsável 2 */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold text-gray-700 mb-3">Responsável 2 (Opcional)</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Nome</Label>
                            <Input
                              value={quickRegisterData.guardian2_name}
                              onChange={(e) => setQuickRegisterData({...quickRegisterData, guardian2_name: e.target.value})}
                              placeholder="Nome completo"
                              data-testid="quick-register-guardian2-name"
                            />
                          </div>
                          <div>
                            <Label>Telefone</Label>
                            <Input
                              value={quickRegisterData.guardian2_phone}
                              onChange={(e) => setQuickRegisterData({...quickRegisterData, guardian2_phone: e.target.value})}
                              placeholder="(00) 00000-0000"
                              data-testid="quick-register-guardian2-phone"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Necessidades especiais e Observações */}
                      <div className="border-t pt-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id="quick-register-special-needs"
                            checked={quickRegisterData.has_special_needs}
                            onCheckedChange={(checked) => setQuickRegisterData({...quickRegisterData, has_special_needs: checked})}
                            data-testid="quick-register-special-needs"
                          />
                          <Label htmlFor="quick-register-special-needs" className="cursor-pointer">
                            Possui necessidades especiais
                          </Label>
                        </div>
                        
                        <div>
                          <Label>Observações</Label>
                          <Textarea
                            value={quickRegisterData.notes}
                            onChange={(e) => setQuickRegisterData({...quickRegisterData, notes: e.target.value})}
                            placeholder="Alergias, restrições, informações importantes..."
                            rows={3}
                            data-testid="quick-register-notes"
                          />
                        </div>
                      </div>
                      
                      {/* Botões */}
                      <div className="flex justify-end space-x-3 pt-4 border-t">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setQuickRegisterDialog(false)}
                          disabled={quickRegisterLoading}
                        >
                          Cancelar
                        </Button>
                        <Button 
                          type="submit" 
                          className="bg-green-600 hover:bg-green-700 text-white"
                          disabled={quickRegisterLoading || !quickRegisterData.gender}
                          data-testid="quick-register-submit"
                        >
                          {quickRegisterLoading ? 'Cadastrando...' : 'Cadastrar e Check-in'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                
                {/* Checkbox Imprimir Etiqueta */}
                <div className="flex items-center gap-2 bg-sky-50 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-sky-200">
                  <input
                    type="checkbox"
                    id="print-on-checkin"
                    checked={printOnCheckin}
                    onChange={(e) => {
                      setPrintOnCheckin(e.target.checked);
                      localStorage.setItem('printOnCheckin', e.target.checked.toString());
                    }}
                    className="w-4 h-4 sm:w-5 sm:h-5 accent-sky-500"
                  />
                  <label htmlFor="print-on-checkin" className="text-xs sm:text-sm font-medium text-sky-700 cursor-pointer flex items-center gap-1">
                    <Printer size={14} className="sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Imprimir etiqueta</span>
                    <span className="sm:hidden">Etiqueta</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="checkin-search relative z-40">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Digite nome, CPF ou telefone..."
                  className="input-field pl-12 text-lg"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="checkin-search-input"
                />
              </div>
              
              {showResults && adolescents.length > 0 && (
                <div className="checkin-results" data-testid="search-results">
                  {adolescents.map((adolescent) => (
                    <div
                      key={adolescent.id}
                      className="checkin-result-item"
                      onClick={() => handleCheckin(adolescent)}
                      data-testid="search-result-item"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {adolescent.photo_url ? (
                            <img src={adolescent.photo_url} alt={adolescent.name} className="avatar" />
                          ) : (
                            <div className="avatar bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center text-blue-700 font-bold text-xl">
                              {adolescent.name.charAt(0)}
                            </div>
                          )}
                          <div>
                            <p className="font-bold text-lg">{adolescent.name}</p>
                            <p className="text-sm text-gray-500">{adolescent.age} anos - {adolescent.phone}</p>
                          </div>
                        </div>
                        <span className={`badge badge-${adolescent.status}`}>
                          {adolescent.status === 'membro' ? 'Membro' : adolescent.status === 'visitante' ? 'Visitante' : 'Visitante 1ª vez'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick Checked-in List */}
          {eventCheckins && eventCheckins.checkins && eventCheckins.checkins.length > 0 && (
            <div className="card relative z-10">
              <div className="flex flex-col gap-3 mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                  Presentes no Evento ({eventCheckins.total})
                </h2>
                
                {/* Campo de busca na lista de check-in */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Buscar na lista de presentes..."
                    value={checkinListSearch}
                    onChange={(e) => setCheckinListSearch(e.target.value)}
                    className="input-field pl-10 pr-10 w-full text-sm"
                    data-testid="checkin-list-search"
                  />
                  {checkinListSearch && (
                    <button
                      onClick={() => setCheckinListSearch('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                
                {/* Botões de seleção e impressão em lote */}
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const allIds = eventCheckins.checkins.slice(0, 20).map(c => c.id);
                      if (selectedForPrint.size === allIds.length) {
                        setSelectedForPrint(new Set());
                      } else {
                        setSelectedForPrint(new Set(allIds));
                      }
                    }}
                    className="flex items-center gap-1 text-xs sm:text-sm"
                  >
                    {selectedForPrint.size === eventCheckins.checkins.slice(0, 20).length ? <CheckSquare size={16} /> : <Square size={16} />}
                    <span>Selecionar</span>
                  </Button>
                  
                  {selectedForPrint.size > 0 && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => {
                          const checkinsToPrintBatch = eventCheckins.checkins.filter(c => selectedForPrint.has(c.id));
                          setCheckinsToPrint(checkinsToPrintBatch);
                          setPrintDialogOpen(true);
                        }}
                        className="btn-primary flex items-center gap-1 text-xs sm:text-sm"
                      >
                        <Printer size={16} />
                        Imprimir ({selectedForPrint.size})
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleMassCheckout}
                        className="flex items-center gap-1 text-xs sm:text-sm text-orange-600 border-orange-300 hover:bg-orange-50"
                        data-testid="mass-checkout-button"
                      >
                        <CheckOut size={16} />
                        Check-out ({selectedForPrint.size})
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {eventCheckins.checkins
                  .filter(checkin => {
                    if (!checkinListSearch) return true;
                    const searchLower = checkinListSearch.toLowerCase();
                    return (
                      (checkin.adolescent_name || '').toLowerCase().includes(searchLower) ||
                      (checkin.guardian_name || '').toLowerCase().includes(searchLower) ||
                      (checkin.sequence_number?.toString() || '').includes(checkinListSearch)
                    );
                  })
                  .slice(0, checkinListSearch ? 100 : 20)
                  .map((checkin, index) => (
                  <div 
                    key={checkin.id} 
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 rounded-lg ${selectedForPrint.has(checkin.id) ? 'bg-sky-50 border border-sky-200' : 'bg-gray-50'}`}
                    data-testid="checkin-item"
                  >
                    <div className="flex items-center gap-3">
                      {/* Checkbox de seleção */}
                      <input
                        type="checkbox"
                        checked={selectedForPrint.has(checkin.id)}
                        onChange={() => {
                          const newSet = new Set(selectedForPrint);
                          if (newSet.has(checkin.id)) {
                            newSet.delete(checkin.id);
                          } else {
                            newSet.add(checkin.id);
                          }
                          setSelectedForPrint(newSet);
                        }}
                        className="w-5 h-5 accent-sky-500 shrink-0"
                      />
                      
                      {/* Número sequencial */}
                      <span className="inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-sky-500 text-white font-bold rounded-lg text-xs sm:text-sm shrink-0">
                        {String(checkin.sequence_number || index + 1).padStart(3, '0')}
                      </span>
                      
                      <div className="min-w-0">
                        <p className="font-semibold text-base sm:text-lg truncate">{checkin.adolescent_name}</p>
                        <p className="text-xs sm:text-sm text-gray-500">
                          Check-in: {format(parseISO(checkin.checkin_time), 'HH:mm', { locale: ptBR })}
                          {checkin.checkout_time && ` | Out: ${format(parseISO(checkin.checkout_time), 'HH:mm', { locale: ptBR })}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:space-x-2 ml-auto sm:ml-0">
                      {/* Badge de classificação - Mobile mostra abreviado */}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        checkin.classification === 'membro' 
                          ? 'bg-green-100 text-green-800' 
                          : checkin.classification === 'visitante_primeira_vez'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-amber-100 text-amber-800'
                      }`}>
                        {checkin.classification === 'membro' 
                          ? 'Membro' 
                          : checkin.classification === 'visitante_primeira_vez'
                            ? '1ª Vez'
                            : 'Visitante'}
                      </span>
                      
                      {/* Botão Etiqueta */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setCheckinsToPrint([checkin]);
                          setPrintDialogOpen(true);
                        }}
                        className="flex items-center gap-1 text-sky-600 border-sky-200 hover:bg-sky-50 px-2 sm:px-3"
                      >
                        <Printer size={14} />
                        <span className="hidden sm:inline">Etiqueta</span>
                      </Button>
                      
                      {/* Botão Check-out */}
                      {checkin.status === 'checked_in' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCheckOut(checkin.id)}
                          className="flex items-center gap-1 px-2 sm:px-3"
                          data-testid="checkout-button"
                        >
                          <CheckOut size={14} />
                          <span className="hidden sm:inline">Check-out</span>
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {!checkinListSearch && eventCheckins.checkins.length > 20 && (
                  <p className="text-center text-gray-500 py-2 text-sm">
                    ... e mais {eventCheckins.checkins.length - 20} registros. Veja todos na aba &quot;Gerenciar Presenças&quot;
                  </p>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="manage">
          {/* Event Filter */}
          <div className="card mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1">
                <Calendar className="text-gray-400" size={20} />
                <select
                  className="input-field flex-1"
                  value={selectedEvent?.id || ''}
                  onChange={(e) => {
                    const event = events.find(ev => ev.id === e.target.value);
                    setSelectedEvent(event);
                  }}
                >
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.name} - {format(parseISO(event.date), "dd/MM/yyyy", { locale: ptBR })} ({event.status || 'agendado'})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Stats for selected event */}
          {eventCheckins && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total de Presenças</p>
                    <p className="text-3xl font-bold text-blue-800">{eventCheckins.total}</p>
                  </div>
                  <Users className="text-blue-400" size={32} />
                </div>
              </div>
              <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <p className="text-sm text-green-600 font-medium">Membros</p>
                <p className="text-3xl font-bold text-green-800">{eventCheckins.members + eventCheckins.visitors}</p>
              </div>
              <div className="card bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <p className="text-sm text-amber-600 font-medium">Visitantes 1ª Vez</p>
                <p className="text-3xl font-bold text-amber-800">{eventCheckins.first_time_visitors}</p>
              </div>
            </div>
          )}

          {/* Search Field for Manage Tab */}
          <div className="card mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar por nome, telefone, responsável..."
                value={manageSearch}
                onChange={(e) => setManageSearch(e.target.value)}
                className="input-field pl-10 w-full"
                data-testid="manage-search-input"
              />
              {manageSearch && (
                <button
                  onClick={() => setManageSearch('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              )}
            </div>
            {manageSearch && eventCheckins?.checkins && (
              <p className="text-sm text-gray-500 mt-2">
                Mostrando {eventCheckins.checkins.filter(c => {
                  const searchLower = manageSearch.toLowerCase();
                  return (
                    (c.adolescent_name || '').toLowerCase().includes(searchLower) ||
                    (c.guardian_name || '').toLowerCase().includes(searchLower) ||
                    (c.phone || '').includes(manageSearch) ||
                    (c.classification || '').toLowerCase().includes(searchLower) ||
                    (c.sequence_number?.toString() || '').includes(manageSearch)
                  );
                }).length} de {eventCheckins.checkins.length} registros
              </p>
            )}
          </div>

          {/* Full Checkins List */}
          <div className="card">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Lista Completa de Presenças
                {selectedEvent && ` - ${selectedEvent.name}`}
              </h2>
              
              {eventCheckins && eventCheckins.checkins && eventCheckins.checkins.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (selectedForPrint.size === eventCheckins.checkins.length) {
                        setSelectedForPrint(new Set());
                      } else {
                        setSelectedForPrint(new Set(eventCheckins.checkins.map(c => c.id)));
                      }
                    }}
                    className="flex items-center gap-1"
                  >
                    {selectedForPrint.size === eventCheckins.checkins.length ? <CheckSquare size={16} /> : <Square size={16} />}
                    <span className="hidden sm:inline">Selecionar Todos</span>
                  </Button>
                  
                  {selectedForPrint.size > 0 && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => {
                          const checkinsToPrintBatch = eventCheckins.checkins.filter(c => selectedForPrint.has(c.id));
                          setCheckinsToPrint(checkinsToPrintBatch);
                          setPrintDialogOpen(true);
                        }}
                        className="btn-primary flex items-center gap-1"
                      >
                        <Printer size={16} />
                        Imprimir ({selectedForPrint.size})
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleMassCheckout}
                        className="flex items-center gap-1 text-orange-600 border-orange-300 hover:bg-orange-50"
                        data-testid="manage-mass-checkout-button"
                      >
                        <CheckOut size={16} />
                        Check-out ({selectedForPrint.size})
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
            
            {loading ? (
              <div className="text-center py-12">Carregando...</div>
            ) : eventCheckins && eventCheckins.checkins && eventCheckins.checkins.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="table w-full">
                  <thead>
                    <tr>
                      <th className="w-8">
                        <input
                          type="checkbox"
                          checked={selectedForPrint.size === eventCheckins.checkins.length}
                          onChange={() => {
                            if (selectedForPrint.size === eventCheckins.checkins.length) {
                              setSelectedForPrint(new Set());
                            } else {
                              setSelectedForPrint(new Set(eventCheckins.checkins.map(c => c.id)));
                            }
                          }}
                          className="w-4 h-4"
                        />
                      </th>
                      <th className="w-16 text-center">Nº</th>
                      <th>Nome</th>
                      <th className="hidden md:table-cell">Check-in</th>
                      <th className="hidden md:table-cell">Check-out</th>
                      <th className="hidden sm:table-cell">Status</th>
                      <th>Classificação</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventCheckins.checkins
                      .filter(checkin => {
                        if (!manageSearch) return true;
                        const searchLower = manageSearch.toLowerCase();
                        return (
                          (checkin.adolescent_name || '').toLowerCase().includes(searchLower) ||
                          (checkin.guardian_name || '').toLowerCase().includes(searchLower) ||
                          (checkin.phone || '').includes(manageSearch) ||
                          (checkin.classification || '').toLowerCase().includes(searchLower) ||
                          (checkin.sequence_number?.toString() || '').includes(manageSearch)
                        );
                      })
                      .map((checkin, index) => (
                      <tr key={checkin.id} className={selectedForPrint.has(checkin.id) ? 'bg-sky-50' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedForPrint.has(checkin.id)}
                            onChange={() => {
                              const newSet = new Set(selectedForPrint);
                              if (newSet.has(checkin.id)) {
                                newSet.delete(checkin.id);
                              } else {
                                newSet.add(checkin.id);
                              }
                              setSelectedForPrint(newSet);
                            }}
                            className="w-4 h-4"
                          />
                        </td>
                        <td className="text-center">
                          <span className="inline-flex items-center justify-center w-10 h-10 bg-sky-500 text-white font-bold rounded-lg text-sm">
                            {String(checkin.sequence_number || index + 1).padStart(3, '0')}
                          </span>
                        </td>
                        <td className="font-semibold">{checkin.adolescent_name}</td>
                        <td className="hidden md:table-cell">{formatDateTime(checkin.checkin_time)}</td>
                        <td className="hidden md:table-cell">{checkin.checkout_time ? formatDateTime(checkin.checkout_time) : '-'}</td>
                        <td className="hidden sm:table-cell">
                          <span className={`badge badge-${checkin.status}`}>
                            {checkin.status === 'checked_in' ? 'Presente' : 'Saiu'}
                          </span>
                        </td>
                        <td>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            checkin.classification === 'membro' 
                              ? 'bg-green-100 text-green-800' 
                              : checkin.classification === 'visitante_primeira_vez'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-amber-100 text-amber-800'
                          }`}>
                            {checkin.classification === 'membro' 
                              ? 'Membro' 
                              : checkin.classification === 'visitante_primeira_vez'
                                ? '1ª Vez'
                                : 'Visitante'}
                          </span>
                        </td>
                        <td>
                          <div className="flex space-x-1">
                            {/* Botão Check-out */}
                            {checkin.status === 'checked_in' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCheckOut(checkin.id)}
                                className="text-orange-600 hover:bg-orange-50"
                                title="Registrar saída"
                                data-testid="manage-checkout-button"
                              >
                                <CheckOut size={16} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setCheckinsToPrint([checkin]);
                                setPrintDialogOpen(true);
                              }}
                              className="text-sky-600 hover:bg-sky-50"
                              title="Imprimir etiqueta"
                            >
                              <Printer size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditCheckin(checkin)}
                              className="text-blue-600 hover:bg-blue-50"
                              title="Editar"
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCheckin(checkin.id)}
                              className="text-red-600 hover:bg-red-50"
                              title="Excluir"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Users size={64} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 text-lg">Nenhum check-in registrado para este evento</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Checkin Dialog */}
      <Dialog open={editCheckinDialog} onOpenChange={setEditCheckinDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Check-in</DialogTitle>
          </DialogHeader>
          
          {selectedCheckin && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-lg">{selectedCheckin.adolescent_name}</p>
                <p className="text-sm text-gray-500">{selectedCheckin.event_name}</p>
              </div>
              
              <div>
                <Label>Data/Hora Check-in</Label>
                <Input
                  type="datetime-local"
                  value={selectedCheckin.checkin_time_local}
                  onChange={(e) => setSelectedCheckin({...selectedCheckin, checkin_time_local: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Data/Hora Check-out</Label>
                <Input
                  type="datetime-local"
                  value={selectedCheckin.checkout_time_local}
                  onChange={(e) => setSelectedCheckin({...selectedCheckin, checkout_time_local: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Status</Label>
                <Select 
                  value={selectedCheckin.status} 
                  onValueChange={(value) => setSelectedCheckin({...selectedCheckin, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checked_in">Presente</SelectItem>
                    <SelectItem value="checked_out">Saiu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is-visitor"
                  checked={selectedCheckin.is_visitor || false}
                  onChange={(e) => setSelectedCheckin({...selectedCheckin, is_visitor: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="is-visitor" className="cursor-pointer">Marcar como Visitante</Label>
              </div>
              
              <div>
                <Label>Observações</Label>
                <Input
                  value={selectedCheckin.notes || ''}
                  onChange={(e) => setSelectedCheckin({...selectedCheckin, notes: e.target.value})}
                  placeholder="Adicione observações..."
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <Button variant="outline" onClick={() => setEditCheckinDialog(false)}>Cancelar</Button>
                <Button className="btn-primary" onClick={handleSaveCheckin}>Salvar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Complete Data Dialog - Modal para completar cadastro antes do check-in */}
      <Dialog open={completeDataDialog} onOpenChange={(open) => {
        if (!open) {
          setCompleteDataDialog(false);
          setAdolescentToCheckin(null);
          setMissingFields([]);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="complete-data-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Edit size={20} />
              Completar Cadastro para Check-in
            </DialogTitle>
          </DialogHeader>
          
          {adolescentToCheckin && (
            <div className="space-y-4">
              {/* Info do adolescente */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-3">
                  {adolescentToCheckin.photo_url ? (
                    <img 
                      src={adolescentToCheckin.photo_url} 
                      alt={adolescentToCheckin.name} 
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold text-lg">
                      {adolescentToCheckin.name?.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-blue-800">{adolescentToCheckin.name}</p>
                    <p className="text-sm text-blue-600">
                      {adolescentToCheckin.age ? `${adolescentToCheckin.age} anos` : ''} 
                      {adolescentToCheckin.phone && ` • ${adolescentToCheckin.phone}`}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Aviso dos campos faltantes */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium mb-2">
                  ⚠️ Os seguintes campos precisam ser preenchidos:
                </p>
                <div className="flex flex-wrap gap-1">
                  {missingFields.map(field => (
                    <span key={field.key} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                      {field.label}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Formulário */}
              <div className="space-y-4">
                {/* Foto */}
                {missingFields.some(f => f.key === 'photo_url') && (
                  <div className="flex justify-center">
                    <PhotoUpload
                      currentPhotoUrl={completeDataForm.photo_url}
                      onPhotoChange={(url) => setCompleteDataForm({...completeDataForm, photo_url: url})}
                    />
                  </div>
                )}
                
                {/* Nome */}
                {missingFields.some(f => f.key === 'name') && (
                  <div>
                    <Label>Nome Completo *</Label>
                    <Input
                      value={completeDataForm.name}
                      onChange={(e) => setCompleteDataForm({...completeDataForm, name: e.target.value})}
                      placeholder="Nome completo do adolescente"
                      data-testid="complete-data-name"
                    />
                  </div>
                )}
                
                {/* Data Nascimento e Gênero */}
                <div className="grid grid-cols-2 gap-3">
                  {missingFields.some(f => f.key === 'birth_date') && (
                    <div>
                      <Label>Data de Nascimento *</Label>
                      <Input
                        type="date"
                        value={completeDataForm.birth_date}
                        onChange={(e) => setCompleteDataForm({...completeDataForm, birth_date: e.target.value})}
                        data-testid="complete-data-birthdate"
                      />
                    </div>
                  )}
                  
                  {missingFields.some(f => f.key === 'gender') && (
                    <div>
                      <Label>Gênero *</Label>
                      <Select 
                        value={completeDataForm.gender} 
                        onValueChange={(value) => setCompleteDataForm({...completeDataForm, gender: value})}
                      >
                        <SelectTrigger data-testid="complete-data-gender">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="masculino">Masculino</SelectItem>
                          <SelectItem value="feminino">Feminino</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                {/* CPF e Telefone */}
                <div className="grid grid-cols-2 gap-3">
                  {missingFields.some(f => f.key === 'cpf') && (
                    <div>
                      <Label>CPF *</Label>
                      <Input
                        value={completeDataForm.cpf}
                        onChange={(e) => setCompleteDataForm({...completeDataForm, cpf: e.target.value})}
                        placeholder="000.000.000-00"
                        data-testid="complete-data-cpf"
                      />
                    </div>
                  )}
                  
                  {missingFields.some(f => f.key === 'phone') && (
                    <div>
                      <Label>Telefone *</Label>
                      <Input
                        value={completeDataForm.phone}
                        onChange={(e) => setCompleteDataForm({...completeDataForm, phone: e.target.value})}
                        placeholder="(00) 00000-0000"
                        data-testid="complete-data-phone"
                      />
                    </div>
                  )}
                </div>
                
                {/* Responsável */}
                {(missingFields.some(f => f.key === 'guardian_name') || missingFields.some(f => f.key === 'guardian_phone')) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-700 mb-3">Dados do Responsável</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {missingFields.some(f => f.key === 'guardian_name') && (
                        <div>
                          <Label>Nome do Responsável *</Label>
                          <Input
                            value={completeDataForm.guardian_name}
                            onChange={(e) => setCompleteDataForm({...completeDataForm, guardian_name: e.target.value})}
                            placeholder="Nome completo"
                            data-testid="complete-data-guardian-name"
                          />
                        </div>
                      )}
                      
                      {missingFields.some(f => f.key === 'guardian_phone') && (
                        <div>
                          <Label>Telefone do Responsável *</Label>
                          <Input
                            value={completeDataForm.guardian_phone}
                            onChange={(e) => setCompleteDataForm({...completeDataForm, guardian_phone: e.target.value})}
                            placeholder="(00) 00000-0000"
                            data-testid="complete-data-guardian-phone"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Botões */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setCompleteDataDialog(false);
                    setAdolescentToCheckin(null);
                    setMissingFields([]);
                  }}
                  disabled={savingCompleteData}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveCompleteData}
                  className="btn-primary"
                  disabled={savingCompleteData}
                  data-testid="complete-data-submit"
                >
                  {savingCompleteData ? 'Salvando...' : 'Salvar e Fazer Check-in'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Labels Dialog */}
      {printDialogOpen && (
        <PrintLabels
          checkins={checkinsToPrint}
          eventName={selectedEvent?.name}
          eventDate={selectedEvent?.date}
          logo={labelLogo}
          onClose={() => setPrintDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default CheckIn;
